import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {finalize} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BusinessDirectoryService} from '../api/business-directory.service';
import {BusinessDirectory, BusinessOpportunity, OpportunityType} from '../models/business-directory';
import {STATES} from '../constants';

export const OPPORTUNITY_TYPES: { value: OpportunityType; label: string }[] = [
    {value: 'COFOUNDER', label: 'Co-founder wanted'},
    {value: 'HIRING', label: 'Hiring'},
    {value: 'SUPPLIER', label: 'Supplier needed'},
    {value: 'INVESTOR', label: 'Investor wanted'},
    {value: 'PARTNER', label: 'Partner wanted'},
    {value: 'OTHER', label: 'Other'},
];

/** Short-lived asks between members. Anyone can read; members post; every reply goes to the author by email. */
@Component({
    selector: 'app-biz-opportunities',
    templateUrl: './biz-opportunities.component.html',
    styleUrls: ['./biz-opportunities.component.css']
})
export class BizOpportunitiesComponent implements OnInit {
    types = OPPORTUNITY_TYPES;
    states = STATES;
    loggedIn = sessionStorage.getItem('username') !== null;
    userId = sessionStorage.getItem('userId') || '';
    isMember = !!JSON.parse(sessionStorage.getItem('rolesPermission') || '{}')?.BUSINESS?.includes('CREATE');

    items: BusinessOpportunity[] = [];
    typeFilter = '';
    q = '';
    loading = false;
    loaded = false;

    formOpen = false;
    draft: BusinessOpportunity = this.emptyDraft();
    myBusinesses: BusinessDirectory[] = [];

    replyingTo: string | null = null;
    replyText = '';

    constructor(private bizService: BusinessDirectoryService, private toastr: ToastrService, private router: Router) {
    }

    ngOnInit(): void {
        this.load();
    }

    private emptyDraft(): BusinessOpportunity {
        return {type: 'COFOUNDER', title: '', description: '', durationDays: 30, businessId: '', city: '', state: ''};
    }

    hasText = (v: unknown): boolean => !!v;

    typeLabel(value: string): string {
        return this.types.find(t => t.value === value)?.label || value;
    }

    daysLeft(o: BusinessOpportunity): number {
        return o.expireAt ? Math.max(0, Math.ceil((o.expireAt * 1000 - Date.now()) / 86_400_000)) : 0;
    }

    load(): void {
        this.loading = true;
        this.bizService.getOpportunities(this.typeFilter || undefined, this.q.trim() || undefined)
            .pipe(finalize(() => {
                this.loading = false;
                this.loaded = true;
            }))
            .subscribe({
                next: list => this.items = list,
                error: err => {
                    console.error('Failed to load opportunities', err);
                    this.toastr.error('Could not load opportunities');
                }
            });
    }

    pickType(value: string): void {
        this.typeFilter = this.typeFilter === value ? '' : value;
        this.load();
    }

    openForm(): void {
        this.formOpen = true;
        if (!this.myBusinesses.length) {
            this.bizService.getMine().subscribe({
                next: list => this.myBusinesses = list,
                error: err => console.error('Failed to load my businesses', err)
            });
        }
    }

    post(): void {
        if (!this.draft.title.trim() || this.draft.description.trim().length < 10) {
            this.toastr.error('Add a title and a short description');
            return;
        }
        const body = {...this.draft, businessId: this.draft.businessId || undefined};
        this.bizService.createOpportunity(body).subscribe({
            next: () => {
                this.toastr.success('Your post is live');
                this.formOpen = false;
                this.draft = this.emptyDraft();
                this.load();
            },
            error: err => this.fail('Could not publish your post', err)
        });
    }

    reply(o: BusinessOpportunity): void {
        if (!this.loggedIn) {
            this.router.navigate(['/login']);
            return;
        }
        this.replyingTo = this.replyingTo === o.opportunityId ? null : o.opportunityId!;
        this.replyText = '';
    }

    sendReply(o: BusinessOpportunity): void {
        if (this.replyText.trim().length < 10) {
            this.toastr.error('Please write a short message (at least 10 characters)');
            return;
        }
        this.bizService.respondToOpportunity(o.opportunityId!, this.replyText.trim()).subscribe({
            next: () => {
                this.toastr.success('Your reply was sent to ' + (o.authorName || 'the author'));
                this.replyingTo = null;
                this.replyText = '';
            },
            error: err => this.fail('Could not send your reply', err)
        });
    }

    remove(o: BusinessOpportunity): void {
        if (!confirm('Delete this post?')) return;
        this.bizService.deleteOpportunity(o.opportunityId!).subscribe({
            next: () => this.items = this.items.filter(i => i.opportunityId !== o.opportunityId),
            error: err => this.fail('Could not delete the post', err)
        });
    }

    toggleHidden(o: BusinessOpportunity): void {
        const hiding = o.status !== 'HIDDEN';
        const reason = hiding ? prompt('Reason for hiding this post (optional):') : null;
        if (hiding && reason === null) return;
        const request = hiding ? this.bizService.hideOpportunity(o.opportunityId!, reason || undefined) : this.bizService.unhideOpportunity(o.opportunityId!);
        request.subscribe({
            next: updated => {
                o.status = updated.status;
                o.hiddenReason = updated.hiddenReason;
            },
            error: err => this.fail('Could not update the post', err)
        });
    }

    private fail(fallback: string, err: any): void {
        console.error(fallback, err);
        this.toastr.error(err?.error?.message || fallback);
    }
}
